import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import styled, { css } from 'styled-components/macro';
import * as cs from '../components/common/common-styles';
import { DEFAULT_FORM_VALUE, TIER_ONE_VALUE, TODAYS_DATE } from '../components/common/constants';
import { LabelledInput } from '../components/common/info-form';
import { BackButton, ContinueButton } from '../components/common/navigation-buttons-v2';
import { VerticalStepper } from '../components/common/vertical-stepper';
import { convertFeeDictToArray, getTBIDTableHeaders } from '../utils/utility-functions';

const Table = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 800px;
  margin-bottom: 40px;
`;

const TableRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 15px 0;

  border-top: ${(props) => props.separator ? '3px solid rgb(244, 244, 244)' : ''};
`;

const textBase = css`
  font-family: 'Lato';
  font-size: 14px;
  line-height: 1.5;
`;

const TableText = styled.div`
  width: 100%;
  text-align: ${(props) => props.isRight ? 'right' : 'left'};

  ${textBase}
  font-weight: ${(props) => !props.isRight || props.isBold ? 'bold' : 'normal'};
  color: #000000;
  margin: auto 0;
`;

const DateText = styled.div`
  ${textBase}
  color: #5c5d5d;
`;

function RowRender(props) {
  const { data, forceBolding, separator } = props;

  return (
    <TableRow separator={separator}>
      {data.map((item, idx) => (
        <TableText key={`${item}-${idx}`} isRight={Boolean(idx)} isBold={forceBolding || !idx}>
          {item}
        </TableText>
      ))}
    </TableRow>
  );
}

const SIGNATORY_LABEL = 'Legal Name of Authorized Signatory';
const DATE_LABEL = 'Date (MM/DD/YYYY)';

export function TbidConfirmation(props) {
  const { payload, updatePayload, businessInfo, licenseFeeInfo } = props;
  const history = useHistory();

  const [isChecked, setIsChecked] = useState(false);
  const [signatory, setSignatory] = useState(payload?.signatory || DEFAULT_FORM_VALUE);

  const tier = businessInfo?.extras?.tier;

  const handleForwardClick = useCallback(() => {
    updatePayload({ signatory });
    history.push('/contact');
    // TODO: Need to set formPayload to post payload
  }, [history, signatory, updatePayload]);

  const tableHeaders = useMemo(
    () => getTBIDTableHeaders(tier === TIER_ONE_VALUE),
    [businessInfo]
  );

  const summaryData = useMemo(
    () => convertFeeDictToArray(licenseFeeInfo.summary, tier),
    [licenseFeeInfo, tier]
  );

  const amountTooSmall = licenseFeeInfo.summary.total <= .50;

  return (
    <VerticalStepper activeStep={2}>
      <cs.Wrapper>
        <cs.HeaderText style={{ paddingBottom: '0px', marginBottom: '40px' }}>
          Please confirm the following information and sign below.
        </cs.HeaderText>

        <Table>
          <RowRender forceBolding={true} data={tableHeaders} />
          {/* TODO: Find a better way to generate a unique key */}
          {licenseFeeInfo.details.map((data) => {
            const feeData = convertFeeDictToArray(data, tier);
            return <RowRender separator key={data.period} data={feeData}/>;
          })}
          <RowRender separator forceBolding={true} data={summaryData} />
        </Table>

        <cs.Form onSubmit={handleForwardClick}>
          <div style={{ display: 'flex', gap: '100px', marginBottom: '20px' }}>
            <LabelledInput
              label={SIGNATORY_LABEL}
              name="signatory"
              value={signatory}
              onChange={(e) => setSignatory(e.target.value)}
              maxLength={250}
              required={true}
              width="250px"
              noMargin
              />
            <div>
              <cs.InputTextFieldLabel style={{ marginBottom: '13px' }}>{DATE_LABEL}</cs.InputTextFieldLabel>
              <DateText>{TODAYS_DATE.toLocaleDateString()}</DateText>
            </div>
          </div>

          <FormControlLabel
            label={
              <cs.BoldedLabel bold={isChecked} style={{ width: '800px' }}>
                I declare under penalty of perjury that the information in this return has been
                examined by me for accuracy and to the best of my knowledge and belief is a true,
                correct, and complete statement of the revenues charged.
                (Certification pursuant to Section 2015.5 of the Civil Code of Procedures.)
              </cs.BoldedLabel>
            }
            labelPlacement="end"
            control={<Checkbox color="default" checked={isChecked} onClick={() => setIsChecked(!isChecked)} />}
          />

          <cs.ButtonWrapper>
            <BackButton />
            <ContinueButton isDisabled={!isChecked || amountTooSmall} />
          </cs.ButtonWrapper>
          {amountTooSmall &&
          <div css="margin-top: 40px; font-size: 12px; color: red">*The Placer County Online Assessment Payment System does not accept payment amounts smaller than $50 cents. Please contact str@placer.ca.gov to make a payment.</div>
          }
        </cs.Form>
      </cs.Wrapper>
    </VerticalStepper>
  );
}
